import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, RefreshCw, Trash2, Sparkles, Clock } from 'lucide-react';
import { IdentityProvidersEmpty } from '@/components/illustrations/IdentityProvidersEmpty';
import { useIdentityProviders, useSyncIdentityProvider, useDeleteIdentityProvider } from '@/hooks/useIdentityProviders';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRelativeDate, formatNumber } from '@/lib/formatters';
import { useEntitlementContext } from '@/contexts/EntitlementContext';
import { IdentityProviderDialog } from './IdentityProviderDialog';
import { EntitlementGate } from '@/components/billing/EntitlementGate';
import { PolicySuggestionDialog } from './PolicySuggestionDialog';
import { ScimConfigPanel } from './ScimConfigPanel';
import type { IdentityProviderType, IdentityProvider } from '@/types/identity-provider';

const providerLabels: Record<IdentityProviderType, string> = {
  azure_entra_id: 'Azure Entra ID',
  aws_iam: 'AWS IAM',
  gcp: 'Google Cloud',
  okta: 'Okta',
};

interface PolicySuggestionTarget {
  idpId: string;
  idpName: string;
}

export function IdentityProvidersPage() {
  const { data: providers, isLoading } = useIdentityProviders();
  const syncMutation = useSyncIdentityProvider();
  const deleteMutation = useDeleteIdentityProvider();
  const { hasFeature } = useEntitlementContext();
  const canSuggestPolicies = hasFeature('policy_studio');
  const hasScim = hasFeature('sso_scim');

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState<IdentityProvider | undefined>();
  const [policyTarget, setPolicyTarget] = useState<PolicySuggestionTarget | null>(null);

  function openCreate() {
    setEditingProvider(undefined);
    setShowDialog(true);
  }

  function openEdit(idp: IdentityProvider) {
    setEditingProvider(idp);
    setShowDialog(true);
  }

  function openPolicySuggestions(idp: IdentityProvider) {
    setPolicyTarget({ idpId: idp.id, idpName: idp.name });
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Connect"
        title="Identity Providers"
        subtitle="Connect identity providers to sync principals, groups, and roles."
        actions={
          <EntitlementGate
            feature="vendor_iam"
            fallback={
              <Button asChild variant="outline">
                <Link to="/directory">Add users in Directory</Link>
              </Button>
            }
          >
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Connect Provider</Button>
          </EntitlementGate>
        }
      />

      {providers && providers.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">Total Providers</p>
            <p className="text-2xl font-semibold tabular-nums mt-1">{providers.length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">Connected</p>
            <p className="text-2xl font-semibold tabular-nums mt-1">{providers.filter(p => p.status === 'connected').length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">Principals synced</p>
            <p className="text-2xl font-semibold tabular-nums mt-1">{formatNumber(providers.reduce((sum, p) => sum + p.principal_count, 0))}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-xs text-muted-foreground">Errors</p>
            <p className={`text-2xl font-semibold tabular-nums mt-1${providers.filter(p => !!p.error_message).length > 0 ? ' text-destructive' : ''}`}>{providers.filter(p => !!p.error_message).length}</p>
          </div>
        </div>
      )}

      {!providers || providers.length === 0 ? (
        <EmptyState
          icon={<IdentityProvidersEmpty className="h-28 w-28 opacity-80" />}
          title="No identity providers connected"
          description="Sync users, groups, and roles from Okta, Entra ID, AWS IAM Identity Center or GCP (Growth and above). On any plan you can add users directly under Directory."
          action={
            <EntitlementGate
              feature="vendor_iam"
              fallback={
                <Button asChild variant="outline">
                  <Link to="/directory">Add users in Directory</Link>
                </Button>
              }
            >
              <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Connect Provider</Button>
            </EntitlementGate>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {providers.map((idp) => {
            const isConnected = idp.status === 'connected';
            const hasPrincipals = idp.principal_count > 0;
            const showSuggestPolicies = isConnected && hasPrincipals;
            const autoSyncEnabled =
              idp.sync_config?.auto_sync && idp.sync_config?.sync_interval_minutes > 0;

            return (
              <div key={idp.id} className="rounded-lg border bg-card p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                      {providerLabels[idp.type]}
                    </p>
                    <h3 className="font-semibold mt-1">{idp.name}</h3>
                  </div>
                  <StatusBadge status={idp.status} />
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Principals</p>
                    <p className="font-medium">{formatNumber(idp.principal_count)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Groups</p>
                    <p className="font-medium">{formatNumber(idp.group_count)}</p>
                  </div>
                </div>

                {idp.last_sync && (
                  <p className="text-xs text-muted-foreground">Last sync: {formatRelativeDate(idp.last_sync)}</p>
                )}

                {autoSyncEnabled && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Auto-sync: every {idp.sync_config?.sync_interval_minutes}m
                  </p>
                )}

                {idp.error_message && (
                  <p className="text-xs text-destructive bg-destructive/10 rounded p-2">{idp.error_message}</p>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={() => openEdit(idp)}>
                    <Pencil className="h-3 w-3 mr-1" />Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => syncMutation.mutate(idp.id)}
                    disabled={syncMutation.isPending}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />Sync
                  </Button>

                  {showSuggestPolicies && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPolicySuggestions(idp)}
                      disabled={!canSuggestPolicies}
                      title={
                        canSuggestPolicies
                          ? 'Generate recommended policy starting points from this IDP'
                          : 'Requires Team plan'
                      }
                    >
                      <Sparkles className="h-3 w-3 mr-1" />Suggest Policies
                    </Button>
                  )}

                  {confirmDelete === idp.id ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => { deleteMutation.mutate(idp.id); setConfirmDelete(null); }}
                    >
                      Confirm Delete
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(idp.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                {/* SCIM section — visible on Growth and above (sso_scim) */}
                {hasScim && (
                  <ScimConfigPanel idpId={idp.id} />
                )}
              </div>
            );
          })}
        </div>
      )}

      <IdentityProviderDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        mode={editingProvider ? 'edit' : 'create'}
        initialData={editingProvider}
      />

      {policyTarget && (
        <PolicySuggestionDialog
          open={policyTarget !== null}
          onOpenChange={(open) => { if (!open) setPolicyTarget(null); }}
          idpId={policyTarget.idpId}
          idpName={policyTarget.idpName}
        />
      )}
    </div>
  );
}
